import { useContext, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { CheckIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  MerchantOrderStatusEnum,
  MerchantOrderTypeEnum,
} from '@/graphql/generatedTypes';
import { useEventItemTypeOptions } from './utils/useEventItemTypeOptions';
import { useUpdateOpsOrderMutation } from '@/graphql/mutations/updateOpsOrder.generated';
import { useUpdateOpsOrderStatusMutation } from '@/graphql/mutations/updateOpsOrderStatus.generated';
import { useUpdateMerchantOrderSharedLinkMutation } from '@/graphql/mutations/updateMerchantOrderSharedLink.generated';
import { useSendToPickingAction } from './hooks/useSendToPickingAction';
import { useRefetchDeliveryNote } from './hooks/useRefetchDeliveryNote';
import {
  ViewerContext,
  CanAccessPartnerData,
} from '@/providers/ViewerContextProvider';
import {
  SHIPPING_LABEL_ELIGIBLE_STATUSES,
  SHIPPING_LABEL_VIEWABLE_STATUSES,
} from './utils/labelStatuses';
import { useGetMerchantOrderSharedLinkLazyQuery } from '@/graphql/queries/getMerchantOrderSharedLink.generated';
import { toast } from 'sonner';
import i18n from '@/utils/i18n';

import type { OpsOrderDetail } from './components/details/types';
import { useEventOrderForm } from './components/details/useEventOrderForm';
import { EventItemLocationCard } from './components/details/EventItemLocationCard';
import { EventOrderSidebar } from './components/details/OrderDetailSidebar';
import { SaveDiscardBottomBar } from './components/details/SaveDiscardBottomBar';

import type { GetEventQuery } from '@/graphql/queries/getEvent.generated';
import { useEventMerchantOrderSharedLinkInfoQuery } from '@/graphql/queries/eventMerchantOrderSharedLinkInfo.generated';
import { CarrierStatusTable } from './components/details';
import { CommentField } from './components/details/DetailFormParts';
import { AddressForm } from '@/components/forms/address/AddressForm';
import { useRefetchOrderDocuments } from './hooks/useRefetchOrderDocuments';
import { isSendCloudCarrier } from '@/components/carrier/carrierLabels';
import { useOrderChangeGuard } from './components/details/useOrderChangeGuard';
import { useWarehousesQuery } from '@/graphql/queries/warehouses.generated';
import { useGetOrderWarehouseConfigQuery } from '@/graphql/queries/getOrderWarehouseConfig.generated';
import { resolveAutomaticWarehouseUuid } from './components/details/warehouseRouting';

type Event = NonNullable<GetEventQuery['event']>;

interface EventOrderDetailsFormProps {
  order: OpsOrderDetail;
  event: Event;
  refetch: () => Promise<unknown>;
}

export function EventOrderDetailsForm({
  order,
  event,
  refetch,
}: EventOrderDetailsFormProps) {
  const [updateStatus, { loading: statusUpdating }] =
    useUpdateOpsOrderStatusMutation();
  const [updateOrder, { loading: saving }] = useUpdateOpsOrderMutation();
  const { handleSendToPicking, pickingLoading: pickingSending } =
    useSendToPickingAction({
      onSuccess: () => {
        toast.success(i18n.t('Order:toastSendToPickingSuccess'));
        refetch();
      },
    });

  const { handleRefetchDeliveryNote, refetchingDeliveryNote } =
    useRefetchDeliveryNote();
  const { handleRefetchOrderDocuments, refetchingOrderDocuments } =
    useRefetchOrderDocuments();
  const viewer = useContext(ViewerContext);
  const canAccessPartnerData = CanAccessPartnerData(viewer);

  const [fetchSharedLink, { loading: zohoFetching }] =
    useGetMerchantOrderSharedLinkLazyQuery({
      fetchPolicy: 'network-only',
    });
  const [updateSharedLink, { loading: salesOrderSending }] =
    useUpdateMerchantOrderSharedLinkMutation();

  const actionLoading = statusUpdating || pickingSending || zohoFetching;

  const isBufferOrder = order.type === MerchantOrderTypeEnum.EventBufferOrder;

  // The event's item type allow list, narrowed by order type: CONTAINER_STATION
  // (empty boxes) on buffer and event account orders, ASSET on buffer orders.
  const { stationOptions, assetOptions } = useEventItemTypeOptions({
    orderType: order.type,
    allowedItemTypes: event.allowed_item_types,
  });

  const { data: sharedLinkData } = useEventMerchantOrderSharedLinkInfoQuery({
    variables: { merchant_order_shared_link_id: order.order_form_id! },
    skip: !order.order_form_id,
  });

  const sharedLinkInfo = sharedLinkData?.eventMerchantOrderSharedLinkInfo;
  const eventAccountName = sharedLinkInfo?.event_account?.name ?? '-';
  const sharedLinkItemLocations =
    sharedLinkInfo?.event_account?.item_locations?.map((loc) => ({
      id: loc.id,
      name: loc.name ?? loc.id,
    })) ?? undefined;

  const { data: warehousesData } = useWarehousesQuery();
  const warehouses = warehousesData?.warehouses ?? [];
  const { data: warehouseRoutingData } = useGetOrderWarehouseConfigQuery();

  const [zohoDialogOpen, setZohoDialogOpen] = useState(false);
  const [zohoSalesOrderUrl, setZohoSalesOrderUrl] = useState<string | null>(
    null,
  );
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [eventAccountEmail, setEventAccountEmail] = useState<string | null>(
    null,
  );
  const {
    form,
    groupedItems,
    setBoxCount,
    incrementBoxCount,
    addItem,
    removeItem,
    addUntrackedItem,
    setUntrackedItemAmount,
    removeUntrackedItem,
    addItemTypeLine,
    setItemTypeLineAmount,
    removeItemTypeLine,
    setComment,
    setOpsComment,
    setStatus,
    setDeliveryDate,
    setCarrierPickupDate,
    setLanguage,
    setWarehouseUuid,
    setCarrier,
    setAddressField,
    isFieldSupported,
    isFieldRequired,
    validate,
    errors,
    isAddressEditable,
    isReadOnly,
    isDirty,
    needsChangeGuard,
    resetToInitial,
    buildUpdateInput,
  } = useEventOrderForm(
    order,
    sharedLinkItemLocations ??
      event.event_participants?.flatMap((p) =>
        (p.item_locations ?? [])
          .filter((loc) => loc.event_id === event.id)
          .map((loc) => ({ id: loc.id, name: loc.name })),
      ) ??
      [],
  );

  const { requestConfirmation, OrderChangeGuardDialog } = useOrderChangeGuard(
    order.comment ?? '',
  );

  const allowedContainerTypes = (event.allowed_container_types ?? []).map(
    (ct) => ({
      id: ct.id,
      name: ct.name,
      imageUrl: ct.image ?? null,
      hexCode: ct.color_hex_code ?? null,
      segment: ct.segment ?? null,
      packingUnit: Math.max(1, ct.packing_unit ?? 1),
    }),
  );

  const handleSendSalesOrder = async () => {
    if (!order.order_form_id) return;
    try {
      await updateSharedLink({
        variables: {
          input: {
            merchant_order_shared_link_id: order.order_form_id,
            // The shared-link mutation only accepts APPROVED_SALES_ORDER_SENT;
            // the backend maps it to the same status as SALES_ORDER_SENT.
            status: MerchantOrderStatusEnum.ApprovedSalesOrderSent,
          },
        },
      });
      toast.success(i18n.t('MerchantOrder:salesOrderSent'));
      setSendDialogOpen(false);
      refetch();
    } catch (err) {
      const reason = getGraphQLErrorReason(err);
      toast.error(
        i18n.t('Common:error'),
        reason ? { description: reason } : undefined,
      );
    }
  };

  // Buffer orders skip Zoho, so no confirmation dialog after approval.
  const showZohoDialogAfterApproval = async (salesOrderUrl?: string | null) => {
    if (isBufferOrder) return;
    let url = salesOrderUrl ?? null;
    if (!url && order.order_form_id) {
      try {
        const response = await fetchSharedLink({
          variables: { id: order.order_form_id },
        });
        url =
          response.data?.getMerchantOrderSharedLink?.sales_order_url ?? null;
      } catch {
        // Dialog still opens; the Zoho button is disabled without a URL.
      }
    }
    setZohoSalesOrderUrl(url);
    setZohoDialogOpen(true);
  };

  const handleSave = async () => {
    const invalidFieldKeys = Object.keys(validate() ?? {});
    if (invalidFieldKeys.length > 0) {
      toast.error(
        i18n.t('MerchantOrder:alertAddressNotComplete', {
          attribute: invalidFieldKeys.join(', '),
        }),
      );
      return;
    }
    let commentOverride: string | undefined;
    if (needsChangeGuard) {
      const result = await requestConfirmation();
      if (!result.confirmed) return;
      commentOverride = result.comment;
    }

    try {
      const update = buildUpdateInput();
      if (commentOverride !== undefined) {
        update.comment = commentOverride;
      }
      const result = await updateOrder({
        variables: { id: order.id, update },
      });
      refetch();
      toast.success(i18n.t('Order:toastSaveSuccess'));
      // buildUpdateInput only includes status when it changed, so this only
      // fires on the transition to Approved.
      if (update.status === MerchantOrderStatusEnum.Approved) {
        const url =
          result.data?.updateOpsOrder?.containers?.find(
            (c) => c.sales_order_url,
          )?.sales_order_url ?? null;
        await showZohoDialogAfterApproval(url);
      }
    } catch {
      toast.error(i18n.t('Order:toastSaveError'));
    }
  };

  // The backend only allows approving an order-form order once the form
  // deadline has passed (the Zoho sales order is created from the final form).
  const formDeadlinePassed = event.merchant_order_form_deadline
    ? new Date(event.merchant_order_form_deadline) < new Date()
    : true;
  const awaitingFormDeadline =
    !isBufferOrder && !!order.order_form_id && !formDeadlinePassed;

  const primaryAction = (() => {
    switch (order.status) {
      case MerchantOrderStatusEnum.OrderReceived:
      case MerchantOrderStatusEnum.PendingApproval:
        return {
          label: awaitingFormDeadline
            ? i18n.t('Order:approveAfterDeadline')
            : i18n.t('Order:approveOrder'),
          disabled: awaitingFormDeadline,
          onClick: async () => {
            try {
              await updateStatus({
                variables: {
                  ids: [order.id],
                  status: MerchantOrderStatusEnum.Approved,
                },
              });
              toast.success(i18n.t('Order:toastApproveSuccess'));
              refetch();
              await showZohoDialogAfterApproval();
            } catch (err) {
              const reason = getGraphQLErrorReason(err);
              toast.error(
                i18n.t('Order:toastApproveError'),
                reason ? { description: reason } : undefined,
              );
            }
          },
        } as const;
      case MerchantOrderStatusEnum.Approved:
        if (isBufferOrder) {
          // Buffer orders skip the Zoho confirmation step
          return {
            label: i18n.t('Order:sendToPicking'),
            disabled: false,
            onClick: () => handleSendToPicking([order.id]),
          } as const;
        }
        return {
          label: i18n.t('MerchantOrder:sendSalesOrder'),
          disabled: !order.order_form_id,
          onClick: async () => {
            if (!order.order_form_id) return;
            try {
              const response = await fetchSharedLink({
                variables: { id: order.order_form_id },
              });
              setEventAccountEmail(
                response.data?.getMerchantOrderSharedLink?.event_account
                  ?.email ?? null,
              );
            } catch {
              setEventAccountEmail(null);
            }
            setSendDialogOpen(true);
          },
        } as const;
      case MerchantOrderStatusEnum.SalesOrderSent:
        return {
          label: i18n.t('Order:sendToPicking'),
          disabled: false,
          onClick: () => handleSendToPicking([order.id]),
        } as const;
      case MerchantOrderStatusEnum.Picked:
        return {
          label: i18n.t('MerchantOrder:OrderStatus.PICKED'),
          disabled: true,
          icon: CheckIcon,
        } as const;
      case MerchantOrderStatusEnum.OnHold:
        return { label: i18n.t('Order:statusOnHold'), disabled: true } as const;
      case MerchantOrderStatusEnum.Canceled:
        return {
          label: i18n.t('MerchantOrder:OrderStatus.CANCELED'),
          disabled: true,
        } as const;
      default:
        return null;
    }
  })();

  // Each actionable primary action ends in an order refetch (Send sales order
  // once its dialog is confirmed), and the refetch resets the form, so pending
  // edits must be saved or discarded first.
  const primaryActionBlockedByUnsavedChanges =
    isDirty && !!primaryAction && 'onClick' in primaryAction;

  // Order documents: the delivery note always; the SendCloud shipping label
  // too when the order ships with a parcel carrier (Logistic Partner orders
  // have no label). Shown once the order is sent to picking; greyed out until
  // picked (both are produced at picking-confirm and regenerated on demand by
  // the mutations). Gated on the same role set as colugo's
  // `canAccessPartnerData`, which both refetch mutations enforce.
  const showDownloadDocuments =
    canAccessPartnerData && SHIPPING_LABEL_ELIGIBLE_STATUSES.has(order.status);
  const documentsViewable = SHIPPING_LABEL_VIEWABLE_STATUSES.has(order.status);
  const hasShippingLabel = isSendCloudCarrier(order.carrier);

  const isSalesOrderSent = [
    MerchantOrderStatusEnum.SalesOrderSent,
    MerchantOrderStatusEnum.SentToPicking,
    MerchantOrderStatusEnum.Picked,
    MerchantOrderStatusEnum.InTransit,
    MerchantOrderStatusEnum.Delivered,
  ].includes(order.status);

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-4 tw:pb-6">
        <div className="tw:flex tw:flex-col tw:gap-2">
          <h1 className="tw:text-4xl tw:font-extrabold">
            {i18n.t('Order:eventOrderPageTitle')}
          </h1>
          <p>{i18n.t('Order:detailPageDescription')}</p>
        </div>
        <div className="tw:flex tw:items-center tw:gap-2">
          {showDownloadDocuments &&
            (hasShippingLabel ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleRefetchOrderDocuments([order.id])}
                disabled={refetchingOrderDocuments || !documentsViewable}
              >
                {refetchingOrderDocuments && <Spinner />}
                {i18n.t('Order:viewOrderDocuments')}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleRefetchDeliveryNote([order.id])}
                disabled={refetchingDeliveryNote || !documentsViewable}
              >
                {refetchingDeliveryNote && <Spinner />}
                {i18n.t('Order:downloadDeliveryNote')}
              </Button>
            ))}
          {primaryAction && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={primaryActionBlockedByUnsavedChanges ? 0 : -1}>
                  <Button
                    size="sm"
                    disabled={
                      primaryAction.disabled ||
                      actionLoading ||
                      primaryActionBlockedByUnsavedChanges
                    }
                    onClick={
                      'onClick' in primaryAction
                        ? primaryAction.onClick
                        : undefined
                    }
                  >
                    {actionLoading ? (
                      <Spinner />
                    ) : (
                      'icon' in primaryAction &&
                      primaryAction.icon && (
                        <primaryAction.icon className="tw:size-4" />
                      )
                    )}
                    {primaryAction.label}
                  </Button>
                </span>
              </TooltipTrigger>
              {primaryActionBlockedByUnsavedChanges && (
                <TooltipContent side="bottom" className="tw:max-w-xs">
                  {i18n.t('Common:saveOrDiscardChangesFirst')}
                </TooltipContent>
              )}
            </Tooltip>
          )}
        </div>
      </div>

      <CarrierStatusTable rows={order.carrier_status} orderId={order.id} />

      <div className="tw:flex tw:items-start tw:gap-4">
        <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-4">
          <Card>
            <CardHeader>
              <CardTitle>
                {i18n.t('Order:sectionGastronomistComment')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CommentField
                value={form.comment}
                onChange={setComment}
                disabled={isReadOnly}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>
                {i18n.t('Order:sectionGastronomistOpsComment')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CommentField
                value={form.opsComment}
                onChange={setOpsComment}
                placeholder={i18n.t('Order:opsCommentPlaceholder')}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>{i18n.t('MerchantOrder:deliveryAddress')}</CardTitle>
            </CardHeader>
            <CardContent className="tw:flex tw:flex-col tw:gap-4">
              {!isAddressEditable && !isReadOnly && (
                <p className="tw:text-sm tw:text-muted-foreground">
                  {i18n.t('Order:eventAddressEditableHint')}
                </p>
              )}
              {isAddressEditable ? (
                <AddressForm
                  value={form.address}
                  errors={errors}
                  isFieldSupported={isFieldSupported}
                  isFieldRequired={isFieldRequired}
                  onFieldChange={setAddressField}
                />
              ) : (
                <AddressForm value={form.address} disabled />
              )}
            </CardContent>
          </Card>
          {groupedItems.map((group) => (
            <EventItemLocationCard
              key={group.itemLocationId ?? '__unassigned__'}
              locationName={group.itemLocationName}
              items={group.containers}
              untrackedItems={group.untrackedItems}
              itemTypeLines={group.itemTypeLines}
              onBoxCountChange={setBoxCount}
              onIncrement={incrementBoxCount}
              onUntrackedAmountChange={setUntrackedItemAmount}
              onItemTypeLineAmountChange={setItemTypeLineAmount}
              onRemoveItem={isReadOnly ? undefined : removeItem}
              onRemoveUntrackedItem={
                isReadOnly ? undefined : removeUntrackedItem
              }
              onRemoveItemTypeLine={isReadOnly ? undefined : removeItemTypeLine}
              allowedContainerTypes={allowedContainerTypes}
              allowedStationTypes={stationOptions}
              allowedAssetTypes={assetOptions}
              isBufferOrder={isBufferOrder}
              eventOrderedTotals={order.event_ordered_totals}
              disabled={isReadOnly}
              onAddItem={
                isReadOnly
                  ? undefined
                  : (typeId, typeName, imageUrl, boxes) => {
                      const ct = event.allowed_container_types?.find(
                        (c) => c.id === typeId,
                      );
                      const packingUnit = Math.max(1, ct?.packing_unit ?? 1);
                      const segment = ct?.segment;
                      addItem({
                        typeId,
                        typeName,
                        imageUrl,
                        hexCode: ct?.color_hex_code ?? null,
                        boxes,
                        packingUnit,
                        segment,
                        itemLocationId: group.itemLocationId,
                        itemLocationName: group.itemLocationName,
                      });
                    }
              }
              onAddUntrackedItem={
                isReadOnly
                  ? undefined
                  : (typeId, typeName, imageUrl) => {
                      addUntrackedItem({
                        typeId,
                        typeName,
                        imageUrl,
                        itemLocationId: group.itemLocationId,
                        itemLocationName: group.itemLocationName,
                      });
                    }
              }
              onAddItemTypeLine={
                isReadOnly
                  ? undefined
                  : (itemTypeId, typeName, imageUrl, hexCode, amount) => {
                      addItemTypeLine({
                        itemTypeId,
                        typeName,
                        imageUrl,
                        hexCode,
                        amount,
                        itemLocationId: group.itemLocationId,
                        itemLocationName: group.itemLocationName,
                      });
                    }
              }
            />
          ))}
        </div>

        <EventOrderSidebar
          status={form.status}
          onStatusChange={setStatus}
          system={order.system}
          orderType={order.type}
          warehouseUuid={form.warehouseUuid}
          automaticWarehouseUuid={
            warehouseRoutingData
              ? resolveAutomaticWarehouseUuid(
                  warehouseRoutingData.getOrderWarehouseConfig.event,
                  form.address.country,
                )
              : order.warehouse_is_automatic
                ? order.warehouse_uuid
                : null
          }
          warehouses={warehouses}
          onWarehouseChange={setWarehouseUuid}
          carrier={form.carrier}
          onCarrierChange={setCarrier}
          carrierService={form.carrierService}
          availableCarrierServices={order.available_carrier_services}
          language={form.language}
          onLanguageChange={setLanguage}
          deadline={order.order_form_deadline}
          carrierPickupDate={form.carrierPickupDate}
          onCarrierPickupDateChange={setCarrierPickupDate}
          eventId={event.id}
          eventName={event.name}
          eventAccountId={sharedLinkInfo?.event_account?.id}
          eventAccountName={eventAccountName}
          isSalesOrderSent={isSalesOrderSent}
          salesOrderUrl={
            order.containers.find((c) => c.sales_order_url)?.sales_order_url ??
            null
          }
          eventDates={{
            start: order.event_start_date,
            end: order.event_end_date,
          }}
          eventManager={order.event_manager}
          deliveryDate={form.deliveryDate}
          onDeliveryDateChange={setDeliveryDate}
          pickingJob={order.picking_job}
          isReadOnly={isReadOnly}
        />
      </div>

      <SaveDiscardBottomBar
        visible={isDirty}
        saving={saving}
        onSave={handleSave}
        onDiscard={resetToInitial}
      />

      <ZohoConfirmDialog
        open={zohoDialogOpen}
        onOpenChange={setZohoDialogOpen}
        salesOrderUrl={zohoSalesOrderUrl}
      />

      <SendSalesOrderDialog
        open={sendDialogOpen}
        onOpenChange={setSendDialogOpen}
        email={eventAccountEmail}
        sending={salesOrderSending}
        onConfirm={handleSendSalesOrder}
      />

      {OrderChangeGuardDialog}
    </div>
  );
}

function ZohoConfirmDialog({
  open,
  onOpenChange,
  salesOrderUrl,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salesOrderUrl: string | null;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{i18n.t('Order:confirmZoho')}</DialogTitle>
          <DialogDescription>
            {i18n.t('Order:zohoConfirmDescription')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{i18n.t('Common:later')}</Button>
          </DialogClose>
          <Button
            disabled={!salesOrderUrl}
            onClick={() => {
              if (salesOrderUrl) window.open(salesOrderUrl, '_blank');
            }}
          >
            {i18n.t('Order:openZohoBooks')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SendSalesOrderDialog({
  open,
  onOpenChange,
  email,
  sending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string | null;
  sending: boolean;
  onConfirm: () => void;
}) {
  const isEmailMissing = !email;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {i18n.t('MerchantOrder:sendSalesOrderTitle')}
          </DialogTitle>
          <DialogDescription>
            {isEmailMissing
              ? i18n.t('MerchantOrder:sendSalesOrderEmailRequired')
              : i18n.t('MerchantOrder:sendSalesOrderDescription', { email })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={sending}>
              {i18n.t('Common:cancel')}
            </Button>
          </DialogClose>
          <Button onClick={onConfirm} disabled={sending || isEmailMissing}>
            {sending && <Spinner />}
            {i18n.t('MerchantOrder:sendSalesOrder')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function getGraphQLErrorReason(err: unknown): string | undefined {
  if (err instanceof ApolloError && err.graphQLErrors.length > 0) {
    const ext = err.graphQLErrors[0].extensions;
    return (
      (ext?.exception as Record<string, string> | undefined)?.reason ??
      (ext?.reason as string | undefined) ??
      err.graphQLErrors[0].message
    );
  }
  return undefined;
}
